/**
 * The arithmetic behind the week and month views.
 *
 * Kept apart from the components and free of React, because every mistake a
 * calendar can make is an arithmetic one — a week that starts on Sunday, a
 * month grid that drops the 31st, two lessons at the same time drawn on top of
 * one another — and none of those are worth discovering by looking at a screen.
 *
 * **Everything here works in London calendar dates, not instants.** A day key
 * is `YYYY-MM-DD` as a person in Wirral would write it, and moving between days
 * is arithmetic on that, done through `Date.UTC` — which has no clock changes,
 * so "a week after the 22nd of March" is the 29th on both sides of the spring
 * change rather than a day that arrives an hour early. Instants only appear
 * when a booking's `startsAt` is placed in a grid, and they are read in
 * Europe/London there for the same reason `format.ts` gives: the lesson happens
 * here whatever the browser's clock thinks.
 *
 * Functions are total. The anchor is held in component state and moved by
 * buttons, but a malformed key must produce an empty grid rather than throw
 * inside a render.
 */

/** A calendar date in Europe/London, `YYYY-MM-DD`. Same shape as `dayKey`. */
export type DayKey = string;

/** Which way the diary is being read. `list` is the original flat diary. */
export type View = "list" | "day" | "week" | "month";

const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

const MS_PER_DAY = 86_400_000;

/** Monday first: a teaching week is Monday to Sunday, not Sunday to Saturday. */
export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

/**
 * Rows in the week grid are quarter hours — the shortest lesson taught here is
 * the fifteen-minute school slot, so anything coarser would stack two of them
 * in one row.
 */
export const SLOT_MINUTES = 15;

/** What the week view shows when the week is empty, so it is a grid and not a gap. */
const DEFAULT_START_HOUR = 9;
const DEFAULT_END_HOUR = 18;

/** Fewer hours than this and the grid reads as a bug rather than a quiet week. */
const MIN_HOURS_SHOWN = 4;

const londonClock = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/London",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

const londonDay = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/London",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** The UTC midnight standing for a London calendar date, or null if unusable. */
function epochOf(key: DayKey): number | null {
  const match = DAY_KEY.exec(key);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const epoch = Date.UTC(year, month - 1, day);

  // Rejects the 31st of February, which `Date.UTC` would roll into March
  // rather than refuse. A grid quietly starting a day late is the sort of
  // thing nobody notices until a lesson is missing from it.
  const rolled = new Date(epoch);
  if (rolled.getUTCFullYear() !== year) return null;
  if (rolled.getUTCMonth() !== month - 1) return null;
  if (rolled.getUTCDate() !== day) return null;

  return epoch;
}

function keyOf(epoch: number): DayKey {
  return londonDay.format(new Date(epoch));
}

/** Whether a string is a day key this module can work with. */
export function isDayKey(value: string): boolean {
  return epochOf(value) !== null;
}

/**
 * Which day of the week a date is, Monday first, or null.
 *
 * The same numbering the rota is stored in, so a working period and a calendar
 * date can be compared without either side converting. Monday is 0 because a
 * teaching week starts on Monday — `getUTCDay` puts Sunday there instead.
 */
export function weekdayIndex(key: DayKey): number | null {
  const epoch = epochOf(key);
  if (epoch === null) return null;
  return (new Date(epoch).getUTCDay() + 6) % 7;
}

/** "Mon", for a day key. Named from the date rather than a column position. */
export function weekdayOf(key: DayKey): string {
  const index = weekdayIndex(key);
  return index === null ? "" : (WEEKDAYS[index] ?? "");
}

/** The day `count` days after `key`. The key itself when it cannot be read. */
export function addDays(key: DayKey, count: number): DayKey {
  const epoch = epochOf(key);
  return epoch === null ? key : keyOf(epoch + count * MS_PER_DAY);
}

/** Whole calendar days between two London dates, independent of clock changes. */
export function daysBetween(from: DayKey, to: DayKey): number | null {
  const first = epochOf(from);
  const last = epochOf(to);
  return first === null || last === null ? null : (last - first) / MS_PER_DAY;
}

/** Monday of the week `key` falls in. */
export function startOfWeek(key: DayKey): DayKey {
  const epoch = epochOf(key);
  if (epoch === null) return key;
  // getUTCDay is 0 for Sunday; shift so Monday is 0.
  const weekday = (new Date(epoch).getUTCDay() + 6) % 7;
  return keyOf(epoch - weekday * MS_PER_DAY);
}

/** The seven days of the week `key` falls in, Monday first. */
export function weekOf(key: DayKey): DayKey[] {
  if (epochOf(key) === null) return [];
  const monday = startOfWeek(key);
  return Array.from({ length: 7 }, (_, index) => addDays(monday, index));
}

/**
 * Every day drawn in the month grid: the month, plus the days either side that
 * complete its first and last weeks.
 *
 * Five rows or six, never a fixed six. A padded row of greyed-out days at the
 * bottom of a February that does not need one makes the month look longer than
 * it is.
 */
export function monthOf(key: DayKey): DayKey[] {
  const epoch = epochOf(key);
  if (epoch === null) return [];

  const date = new Date(epoch);
  const first = keyOf(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
  const last = keyOf(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));

  const start = startOfWeek(first);
  const end = addDays(startOfWeek(last), 6);

  const days: DayKey[] = [];
  for (let day = start; day <= end; day = addDays(day, 1)) days.push(day);
  return days;
}

/** Whether two days fall in the same calendar month. */
export function isSameMonth(a: DayKey, b: DayKey): boolean {
  return a.slice(0, 7) === b.slice(0, 7);
}

/** The anchor a step moves to: a day for a day, a week for a week, and so on. */
export function shift(view: View, anchor: DayKey, steps: number): DayKey {
  if (view === "day") return addDays(anchor, steps);
  if (view === "week") return addDays(anchor, steps * 7);

  const epoch = epochOf(anchor);
  if (epoch === null) return anchor;

  const date = new Date(epoch);
  // The first of the month, then moved by whole months: stepping from the 31st
  // would otherwise skip the months that have no 31st.
  return keyOf(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + steps, 1));
}

/**
 * The window to ask the server for, so the whole grid is filled.
 *
 * Padded a day either side. `/admin/bookings` bounds the window at UTC
 * midnight while the grid is drawn in London days, and during British Summer
 * Time those are an hour apart — so an unpadded request would drop anything in
 * the first London hour of the first day. Nothing is taught at half past
 * midnight, but a grid that is right by luck is not right.
 *
 * The extra days never reach the screen: both views place a booking by its
 * London day key, and a key outside the grid has no cell to be drawn in.
 */
export function windowFor(view: View, anchor: DayKey): { from: DayKey; to: DayKey } {
  const days = daysShown(view, anchor);
  const first = days[0];
  const last = days[days.length - 1];
  if (first === undefined || last === undefined) return { from: anchor, to: anchor };
  return { from: addDays(first, -1), to: addDays(last, 1) };
}

// ── Placing bookings in the grid ─────────────────────────────────────────────

/**
 * What the grid needs in order to place something.
 *
 * Deliberately only these three. A colleague's lesson has no booking id and no
 * student, and the grid is none the wiser: it places a box for anything that
 * belongs to an appointment and happens between two instants.
 */
export interface Placeable {
  appointmentId: string;
  startsAt: string;
  endsAt: string;
}

/**
 * One appointment, with everybody booked into it.
 *
 * The API returns a row per participant, which is right — a group session is
 * several children and a waiting-list entry is a real thing to see. Drawn
 * literally, though, a group of four becomes four boxes at nine o'clock, and a
 * tutor reading their morning sees four lessons where they have one. The list
 * view can afford a row each because it is a list; a grid cannot.
 */
export interface Session<T extends Placeable> {
  appointmentId: string;
  startsAt: string;
  endsAt: string;
  /** Minutes from London midnight, for placing the box. */
  startMinutes: number;
  endMinutes: number;
  bookings: T[];
}

/** Minutes from midnight in London, for an instant. */
export function minutesOfDay(iso: string): number {
  const parts = londonClock.format(new Date(iso)).split(":");
  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;
  // h23 gives 00 for midnight, but a formatter configured elsewhere might give
  // 24; folding it costs a line and stops a midnight lesson landing off-grid.
  return (hours % 24) * 60 + minutes;
}

/** The London day a booking belongs to. */
export function dayOf(iso: string): DayKey {
  return londonDay.format(new Date(iso));
}

/**
 * Group bookings into appointments, in time order, one list per column.
 *
 * What a column *is* is the caller's business. The week has one per day; the
 * day view has one per tutor. The arithmetic underneath — collapsing a group
 * appointment into one session, working out where its box starts and ends,
 * putting them in time order — is the same either way, and is the reason there
 * is one function rather than two that drift.
 *
 * Only the columns asked for come back, each with an entry even when nothing
 * is in it: a calendar with holes where the empty columns should be is not a
 * calendar, and a tutor with a free morning is exactly what somebody is
 * looking for.
 */
export function sessionsByColumn<T extends Placeable>(
  bookings: readonly T[],
  columns: readonly string[],
  columnOf: (booking: T) => string,
): Map<string, Session<T>[]> {
  const wanted = new Set(columns);
  const byAppointment = new Map<string, { column: string; session: Session<T> }>();

  for (const booking of bookings) {
    const column = columnOf(booking);
    if (!wanted.has(column)) continue;

    const existing = byAppointment.get(booking.appointmentId);
    if (existing) {
      existing.session.bookings.push(booking);
      continue;
    }

    const startMinutes = minutesOfDay(booking.startsAt);
    byAppointment.set(booking.appointmentId, {
      column,
      session: {
        appointmentId: booking.appointmentId,
        startsAt: booking.startsAt,
        endsAt: booking.endsAt,
        startMinutes,
        // An appointment ending after midnight would compute an end before its
        // own start; nothing here runs that late, and clamping is cheaper than
        // a box drawn upside down.
        endMinutes: Math.max(startMinutes + SLOT_MINUTES, minutesOfDay(booking.endsAt)),
        bookings: [booking],
      },
    });
  }

  const grid = new Map<string, Session<T>[]>();
  for (const column of columns) grid.set(column, []);

  for (const { column, session } of byAppointment.values()) grid.get(column)?.push(session);

  for (const sessions of grid.values()) {
    sessions.sort((a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes);
  }

  return grid;
}

/** The same, with a column per London day. What the week and month are built from. */
export function sessionsByDay<T extends Placeable>(
  bookings: readonly T[],
  days: readonly DayKey[],
): Map<DayKey, Session<T>[]> {
  return sessionsByColumn(bookings, days, (booking) => dayOf(booking.startsAt));
}

/** A session with its place in a day column worked out. */
export interface Placed<T extends Placeable> {
  session: Session<T>;
  /** Which column within the day, from zero. */
  lane: number;
  /** How many columns the day is split into where this session sits. */
  lanes: number;
}

/**
 * Share a day's width between lessons happening at once.
 *
 * Two lessons can overlap honestly — a tutor's own lesson and a school session
 * somebody else is covering both land on their diary — and one drawn over the
 * other hides a clash, which is the single thing a tutor most needs to see.
 *
 * Sessions are walked in start order and put in the first lane whose last
 * session has finished. Lane counts are then taken per cluster of overlapping
 * sessions, not per day: one clash at nine o'clock should not narrow every
 * other lesson in the day to half width.
 */
export function layOutDay<T extends Placeable>(sessions: readonly Session<T>[]): Placed<T>[] {
  const placed: Placed<T>[] = [];
  const laneEnds: number[] = [];

  // Sessions in the cluster being built, by index into `placed`.
  let cluster: number[] = [];
  let clusterEnd = -1;

  const closeCluster = () => {
    const lanes = Math.max(1, ...cluster.map((index) => (placed[index]?.lane ?? 0) + 1));
    for (const index of cluster) {
      const entry = placed[index];
      if (entry) entry.lanes = lanes;
    }
    cluster = [];
    laneEnds.length = 0;
  };

  for (const session of sessions) {
    // Nothing still running: the previous cluster is finished and its width is
    // settled without the next lesson affecting it.
    if (session.startMinutes >= clusterEnd && cluster.length > 0) closeCluster();

    let lane = laneEnds.findIndex((end) => end <= session.startMinutes);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = session.endMinutes;

    placed.push({ session, lane, lanes: 1 });
    cluster.push(placed.length - 1);
    clusterEnd = Math.max(clusterEnd, session.endMinutes);
  }

  if (cluster.length > 0) closeCluster();
  return placed;
}

/** The band of hours a week grid covers. */
export interface HourRange {
  startHour: number;
  endHour: number;
}

/**
 * Which hours to draw, from what is actually booked that week.
 *
 * A fixed midnight-to-midnight grid would spend most of its height on hours
 * nobody teaches in, leaving the lessons squeezed into a strip. Taking the
 * range from the week means an evening of teaching gets the screen and a
 * Saturday morning gets it too — at the cost of the grid changing height
 * between weeks, which is the better trade for a diary that is read rather
 * than compared.
 */
export function hoursShown(
  sessions: readonly Session<Placeable>[],
  wholeDay = false,
): HourRange {
  // Midnight to midnight, whatever is booked. The day view asks for this: the
  // hours nobody is teaching in are the ones somebody planning a day is
  // looking at, and cropping to the busy part hides exactly that.
  if (wholeDay) return { startHour: 0, endHour: 24 };

  if (sessions.length === 0) {
    return { startHour: DEFAULT_START_HOUR, endHour: DEFAULT_END_HOUR };
  }

  let earliest = Infinity;
  let latest = -Infinity;
  for (const session of sessions) {
    earliest = Math.min(earliest, session.startMinutes);
    latest = Math.max(latest, session.endMinutes);
  }

  const startHour = Math.max(0, Math.floor(earliest / 60));
  // A lesson ending at 17:00 needs the grid to reach 17:00, not past it.
  let endHour = Math.min(24, Math.ceil(latest / 60));

  if (endHour - startHour < MIN_HOURS_SHOWN) {
    endHour = Math.min(24, startHour + MIN_HOURS_SHOWN);
  }

  return { startHour, endHour };
}

/** Where a whole-day grid opens when nothing at all is booked in it. */
const OPENS_AT_MINUTES = 9 * 60;

/**
 * The minute a whole-day grid should be scrolled to.
 *
 * Midnight to midnight is most of two thousand pixels and the teaching is a
 * band in the middle of it, so a grid that opened at the top would open on
 * hours nobody works in and make every visit start with a scroll.
 *
 * The morning is a fallback for a day with nothing in it, **not** a ceiling.
 * Written as `Math.min(...starts, MORNING)` it silently became one — every day
 * then opened at nine however late the teaching was, and an evening lesson sat
 * below the fold on a page that looked empty.
 */
export function opensAt(sessions: readonly Session<Placeable>[]): number {
  if (sessions.length === 0) return OPENS_AT_MINUTES;
  return Math.min(...sessions.map((session) => session.startMinutes));
}

/**
 * Where a session sits in the quarter-hour rows, as a CSS `grid-row` pair.
 *
 * Rows are one-based and the end is exclusive, which is what `grid-row` wants.
 * A start is floored to its quarter and an end raised to the next one, so a
 * lesson at 09:05 keeps its whole length instead of losing five minutes off
 * each end and a ten-minute lesson never collapses to nothing.
 */
export function rowsFor(
  /* Only the two minute fields, so that anything with a start and an end can
     be placed — a session, or a band of working hours — without inventing an
     appointment to carry it. */
  span: { startMinutes: number; endMinutes: number },
  range: HourRange,
): { start: number; end: number } {
  const top = range.startHour * 60;
  const start = Math.floor((span.startMinutes - top) / SLOT_MINUTES);
  const end = Math.ceil((span.endMinutes - top) / SLOT_MINUTES);
  const rows = ((range.endHour - range.startHour) * 60) / SLOT_MINUTES;

  const clampedStart = Math.min(Math.max(start, 0), rows - 1);
  const clampedEnd = Math.min(Math.max(end, clampedStart + 1), rows);

  return { start: clampedStart + 1, end: clampedEnd + 1 };
}

/**
 * The days a view draws, which is what the grid is built from and what the
 * count at the top of the page is counted over.
 *
 * The list has none: it draws whatever the date range returned, and is the one
 * view whose extent is typed in rather than derived from an anchor.
 */
export function daysShown(view: View, anchor: DayKey): DayKey[] {
  if (view === "day") return isDayKey(anchor) ? [anchor] : [];
  if (view === "week") return weekOf(anchor);
  if (view === "month") return monthOf(anchor);
  return [];
}
